import React from "react";
import { useTranslation } from "react-i18next";

import {
    Card,
    CardTitle,
    LanguageTabs,
    LanguageTab,
    ColumnHeaders,
    ColumnHeaderLabel,
    ResetButton,
    InfoRow,
    InfoRowLabel,
    InfoInput,
    InfoActions,
    EyeButton,
    MiniToggle,
    ResetIconButton,
} from "./styled";
import { LANGUAGES, RestaurantText } from "./types";

type RestaurantInfoCardProps = {
    mainColor: string;
    selectedLangRestaurant: string;
    onSelectLangRestaurant: (code: string) => void;
    currentTexts: RestaurantText;
    currentDefaults: RestaurantText;
    nameVisible: boolean;
    descriptionVisible: boolean;
    onRestaurantTextChange: (field: "name" | "description", value: string) => void;
    onToggleNameVisible: () => void;
    onToggleDescriptionVisible: () => void;
    onResetRestaurantField: (field: "name" | "description") => void;
    onResetAllRestaurant: () => void;
};

export function RestaurantInfoCard({
    mainColor,
    selectedLangRestaurant,
    onSelectLangRestaurant,
    currentTexts,
    currentDefaults,
    nameVisible,
    descriptionVisible,
    onRestaurantTextChange,
    onToggleNameVisible,
    onToggleDescriptionVisible,
    onResetRestaurantField,
    onResetAllRestaurant,
}: RestaurantInfoCardProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <Card data-testid="scan-to-misc-restaurant-info-card">
            <CardTitle>{t("Titre et Description")}</CardTitle>

            <LanguageTabs data-testid="scan-to-misc-restaurant-language-tabs">
                {LANGUAGES.map((lang) => (
                    <LanguageTab
                        key={lang.code}
                        data-testid={`scan-to-misc-restaurant-lang-tab-${lang.code}`}
                        isActive={selectedLangRestaurant === lang.code}
                        mainColor={mainColor}
                        onClick={() => onSelectLangRestaurant(lang.code)}
                    >
                        {lang.label}
                    </LanguageTab>
                ))}
            </LanguageTabs>

            <ColumnHeaders>
                <div />
                <ColumnHeaderLabel>{t("Texte par défaut")}</ColumnHeaderLabel>
                <ColumnHeaderLabel>{t("Personnalisé")}</ColumnHeaderLabel>
                <ResetButton data-testid="scan-to-misc-reset-all-restaurant-btn" mainColor={mainColor} onClick={onResetAllRestaurant}>
                    <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "currentColor", marginRight: 4, verticalAlign: "middle" }}>
                        <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
                    </svg>
                    {t("Réinitialiser")}
                </ResetButton>
            </ColumnHeaders>

            {/* Titre row */}
            <InfoRow data-testid="scan-to-misc-title-row">
                <InfoRowLabel>{t("Titre")}</InfoRowLabel>
                <InfoInput
                    data-testid="scan-to-misc-title-default-input"
                    value={currentDefaults.name}
                    disabled
                />
                <InfoInput
                    data-testid="scan-to-misc-title-custom-input"
                    value={currentTexts.name}
                    onChange={(e) => onRestaurantTextChange("name", e.target.value)}
                />
                <InfoActions>
                    <EyeButton
                        data-testid="scan-to-misc-title-visibility-btn"
                        isVisible={nameVisible}
                        mainColor={mainColor}
                        onClick={onToggleNameVisible}
                        title={nameVisible ? t("Visible") : t("Hidden")}
                    >
                        {nameVisible ? (
                            <svg viewBox="0 0 24 24">
                                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                            </svg>
                        ) : (
                            <svg viewBox="0 0 24 24">
                                <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                            </svg>
                        )}
                    </EyeButton>
                    <MiniToggle
                        data-testid="scan-to-misc-title-toggle"
                        isActive={nameVisible}
                        mainColor={mainColor}
                        onClick={onToggleNameVisible}
                    />
                    <ResetIconButton
                        data-testid="scan-to-misc-reset-title-btn"
                        mainColor={mainColor}
                        onClick={() => onResetRestaurantField("name")}
                        title={t("Reset")}
                    >
                        <svg viewBox="0 0 24 24">
                            <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
                        </svg>
                    </ResetIconButton>
                </InfoActions>
            </InfoRow>

            {/* Description row */}
            <InfoRow data-testid="scan-to-misc-description-row">
                <InfoRowLabel>{t("Description")}</InfoRowLabel>
                <InfoInput
                    data-testid="scan-to-misc-description-default-input"
                    value={currentDefaults.description}
                    disabled
                />
                <InfoInput
                    data-testid="scan-to-misc-description-custom-input"
                    value={currentTexts.description}
                    onChange={(e) => onRestaurantTextChange("description", e.target.value)}
                />
                <InfoActions>
                    <EyeButton
                        data-testid="scan-to-misc-description-visibility-btn"
                        isVisible={descriptionVisible}
                        mainColor={mainColor}
                        onClick={onToggleDescriptionVisible}
                        title={descriptionVisible ? t("Visible") : t("Hidden")}
                    >
                        {descriptionVisible ? (
                            <svg viewBox="0 0 24 24">
                                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                            </svg>
                        ) : (
                            <svg viewBox="0 0 24 24">
                                <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                            </svg>
                        )}
                    </EyeButton>
                    <MiniToggle
                        data-testid="scan-to-misc-description-toggle"
                        isActive={descriptionVisible}
                        mainColor={mainColor}
                        onClick={onToggleDescriptionVisible}
                    />
                    <ResetIconButton
                        data-testid="scan-to-misc-reset-description-btn"
                        mainColor={mainColor}
                        onClick={() => onResetRestaurantField("description")}
                        title={t("Reset")}
                    >
                        <svg viewBox="0 0 24 24">
                            <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
                        </svg>
                    </ResetIconButton>
                </InfoActions>
            </InfoRow>
        </Card>
    );
}
